import {
  Carousel,
  CarouselNavButtons,
  useCarousel,
} from "@/components/ui/Carousel";
import { StarsIcon, VerifiedIcon } from "@/components/common/Icons";
import { Button } from "@/components/ui/Button";

/**
 * TestimonialCard Component
 */
function TestimonialCard({ testimonial }) {
  return (
    <div
      className="w-[420px] min-w-[420px] bg-white rounded-[1.5rem] p-6 flex flex-col gap-4 min-h-[280px]
                    max-md:w-full max-md:min-w-full max-md:p-5 max-md:rounded-[1.2rem] max-md:gap-3 max-md:min-h-[270px]"
    >
      <div className="flex gap-4 max-md:gap-3">
        {/* Reviewer Info */}
        <div className="flex-1 flex flex-col justify-between">
          <StarsIcon className="w-20 h-5 mb-0.5" />
          <h3 className="text-xl font-semibold leading-tight max-md:text-[1.1rem] max-md:leading-[1.3]">
            {testimonial.headline}
          </h3>
          <div className="mt-0.5 flex flex-col gap-2 text-[0.85rem]">
            <span className="font-medium text-[1.05rem] max-md:font-medium">
              {testimonial.name}
            </span>
            <div className="flex items-center gap-1 text-[0.85rem] text-green max-md:text-[0.8rem]">
              <VerifiedIcon
                className="w-4 h-4 text-green"
                color="var(--color-green)"
              />
              <span>Verified Buyer</span>
            </div>
          </div>
        </div>

        {/* Reviewer Image */}
        <div
          className="w-[90px] h-[120px] rounded-[0.8rem] overflow-hidden bg-light-gray
                        max-md:w-[80px] max-md:h-[100px]"
        >
          <img
            src={testimonial.image}
            alt={testimonial.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      </div>

      <p className="text-gray leading-relaxed max-md:text-[0.95rem] max-md:leading-[1.45]">
        {testimonial.review}
      </p>
    </div>
  );
}

/**
 * PromoCard Component
 */
function PromoCard() {
  const promoCardBg = `${window.template_root}/src/public/img/promo.webp`;

  const acfData = window.post_template || [];
  const ctaLink = window.cta_link || "#";

  return (
    <div
      className="w-[420px] min-w-[420px] bg-(image:--promoCardBg) bg-cover bg-center rounded-[1.5rem]
                    p-8 flex flex-col items-center justify-center text-center gap-2.5 text-white
                    min-h-[280px] relative overflow-hidden
                    max-md:w-full max-md:min-w-full max-md:p-6 max-md:rounded-[1.2rem] max-md:gap-2 max-md:min-h-[270px]"
      style={{ "--promoCardBg": `url('${promoCardBg}')` }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 promo-overlay z-[1] rounded-[1.5rem] max-md:rounded-[1.2rem]" />

      {/* Content */}
      <div className="relative z-[2] flex flex-col items-center gap-2.5">
        <h3 className="font-family-title text-[2rem] font-normal m-0 leading-tight max-md:text-[1.85rem]">
          Ready to Join Them?
        </h3>
        <p className="text-base m-0 opacity-80 leading-relaxed">
          See how thousands found their match — take the quiz to find yours.
        </p>
        <Button
          href={ctaLink}
          className="mt-3 py-3 px-6 bg-secondary text-green max-md:py-3.5 max-md:px-8 max-md:mt-3"
        >
          Take the Quiz
        </Button>
      </div>
    </div>
  );
}

/**
 * TestimonialsSection Component
 */
export function TestimonialsSection({ testimonials }) {
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 767;

  const { emblaRef, scrollPrev, scrollNext } = useCarousel({
    align: "center",
    dragFree: !isMobile,
    loop: true,
    containScroll: false,
  });

  return (
    <section
      id="reviews"
      className="py-16 pb-12 bg-accent-bg max-md:py-8 max-md:pb-6"
    >
      {/* Header */}
      <div className="text-center mb-8 px-4 max-md:mb-6">
        <h2 className="font-family-title text-[2.5rem] font-normal mb-2 max-md:text-[1.8rem] max-md:leading-[1.25] max-md:mb-2">
          Thousands of Happy Clients
        </h2>
        <p className="text-[1.1rem] text-gray max-w-[500px] mx-auto leading-relaxed max-md:text-[0.95rem]">
          This could be you - check out these reviews from happy customers
          around the world
        </p>
      </div>

      {/* Carousel */}
      <div className="embla max-w-full mx-auto p-0">
        <div
          ref={emblaRef}
          className="embla__viewport overflow-hidden cursor-grab select-none"
        >
          <div className="embla__container flex gap-6 max-md:gap-4">
            {/* First slide margin for loop effect */}
            <div className="embla__slide flex-[0_0_auto] ml-4 max-md:ml-4 max-md:flex-[0_0_85%]">
              <TestimonialCard testimonial={testimonials[0]} />
            </div>

            {testimonials.slice(1).map((testimonial, index) => (
              <div
                key={testimonial.id}
                className="embla__slide flex-[0_0_auto] max-md:flex-[0_0_85%]"
              >
                <TestimonialCard testimonial={testimonial} />
              </div>
            ))}

            {/* Promo Card */}
            <div className="embla__slide flex-[0_0_auto] max-md:flex-[0_0_85%]">
              <PromoCard />
            </div>
          </div>
        </div>

        {/* Navigation */}
        <CarouselNavButtons
          onPrev={scrollPrev}
          onNext={scrollNext}
          className="max-md:mt-5"
        />
      </div>
    </section>
  );
}

export default TestimonialsSection;
